<template>
  <div class="map-container">
    <svg-map
      :map="World"
      :location-class="getLocationClass"
      @mouseover="pointLocation"
      @mouseout="unpointLocation"
      @mousemove="moveOnLocation"
    />
    <div class="map-tooltip" :style="tooltipStyle">
      {{ pointedLocation }}
    </div>
  </div>
</template>

<script>
import World from "@svg-maps/world";
import { SvgMap } from "vue-svg-map";
import chroma from "chroma-js";
import { getDynamicMapCss } from "@/assets/js/components/dynamic-map-css";

export default {
  name: "Map",
  components: {
    SvgMap
  },
  props: {
    dates: {
      type: Array,
      required: true
    },
    selectedDomains: {
      type: Array,
      required: true
    },
    status: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      World,
      cssClasses: [],
      pointedLocation: null,
      tooltipStyle: { display: "none" },
      lowColor: "#fde2e2",
      highColor: "#d83737",
      defaultCountryFillColor: "#dadada",
      countryStrokeColor: "black",
      countryData: {},
      loading: false,
      total: null,
      banned: null,
      timestamp: 0
    };
  },
  watch: {
    async dates() {
      await this.getCountriesChartData();
    },
    async selectedDomains() {
      await this.getCountriesChartData();
    },
    async status(newVal) {
      if (this.total === null && this.banned === null) {
        await this.getCountriesChartData();
      } else {
        if (newVal === true) {
          this.countryData = this.banned;
        } else {
          this.countryData = this.total;
        }
        this.renderMapCSS();
      }
    },
    cssClasses() {
      this.updateLocationClass();
    }
  },
  mounted() {
    this.getCountriesChartData();
  },
  methods: {
    getColor(item) {
      if (item.value === 0) {
        return this.defaultCountryFillColor;
      }
      return item.color;
    },
    pointLocation(event) {
      this.pointedLocation = this.getLocationName(event.target);
    },
    unpointLocation() {
      this.pointedLocation = null;
      this.tooltipStyle = { display: "none" };
    },
    moveOnLocation(event) {
      this.tooltipStyle = {
        display: "block",
        top: `${event.offsetY - 40}px`,
        left: `${event.offsetX}px`
      };
    },
    updateLocationClass(location = "") {
      if (location) {
        let obj = this.cssClasses.find(
          o => o.name === location.id && o.value !== 0
        );
        if (obj) {
          document.getElementById(location.id).style.fill = obj.color;
        }
      } else {
        for (let loc of this.cssClasses) {
          if (loc.value != 0) {
            document.getElementById(loc.name)
              ? (document.getElementById(loc.name).style.fill = loc.color)
              : "";
          } else {
            document.getElementById(loc.name)
              ? (document.getElementById(loc.name).style.fill = "")
              : "";
          }
        }
      }
    },
    getLocationClass(location) {
      this.updateLocationClass(location);
      return "";
    },
    getLocationName(node) {
      if (!node) {
        return;
      }
      return (
        node.attributes.name.value +
        ": " +
        this.countryData[node.attributes.id.value]
      );
    },
    renderMapCSS() {
      this.cssClasses = getDynamicMapCss(
        this.countryData,
        chroma.scale([this.lowColor, this.highColor])
      );
    },
    async getCountriesChartData() {
      this.loading = true;
      try {
        const {
          total,
          banned,
          timestamp
        } = await this.$api.analyticsCountries.getCountriesChart({
          dateFrom: this.dates[0],
          dateTo: this.dates[1] ?? this.dates[0],
          domains: this.selectedDomains.map(el => el.id),
          type: "countries"
        });

        if (timestamp > this.timestamp) {
          this.total = total;
          this.banned = banned;
          this.countryData = banned;
          this.timestamp = timestamp;
          this.renderMapCSS();
        }
      } catch (e) {
        switch (e.response.status) {
          case 401: {
            await this.$api.auth.logout();
          }
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style>
.svg-map {
  width: auto;
  max-height: 600px;
  stroke: #666;
  stroke-width: 1;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.svg-map__location {
  fill: #dadada;
  cursor: pointer;
}

.svg-map__location:focus,
.svg-map__location:hover {
  fill: #c9c9c9;
  outline: 0;
}

.map-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 10px;
  border-radius: 4px;
}
</style>
